import {
  Grid,
  GridItem,
  Heading,
  Text,
  useMediaQuery,
  theme,
  Container,
  Box,
} from '@everlywell/ui-kit';
import Carousel from 'components/Carousel';
import GridCard, { GridCardProps, GridCardSlotOverrides } from 'components/GridCard';
import React from 'react';

// Types ----------

type SlotOverride = {
  sx?: any;
};

type GridCardListHeaderSlotOverrides = {
  container?: SlotOverride;
  eyebrow?: SlotOverride;
  title?: SlotOverride;
};

type GridCardListMobileSlotOverrides = {
  container?: SlotOverride;
  carousel?: SlotOverride;
};

type GridCardListDesktopSlotOverrides = {
  grid?: SlotOverride;
  item?: SlotOverride;
  itemFullWidth?: SlotOverride;
};

export type GridCardListSlotOverrides = {
  root?: SlotOverride;
  grid?: SlotOverride;
  header?: GridCardListHeaderSlotOverrides;
  content?: SlotOverride;
  mobile?: GridCardListMobileSlotOverrides;
  desktop?: GridCardListDesktopSlotOverrides;
  card?: GridCardSlotOverrides;
};

export interface GridCardListProps {
  cards: GridCardProps[];
  eyebrow: string;
  title: string;
  sx?: any;
  slots?: GridCardListSlotOverrides;
}

// GridCardList ------

const GridCardList: React.FC<GridCardListProps | null> = (props) => {
  const [isMobile] = useMediaQuery(`(max-width: ${theme.breakpoints.tablet})`);

  if (!props) {
    return null;
  }

  const { cards, eyebrow, title, sx, slots } = props;

  const oddNumberOfCards = cards.length % 2 !== 0;

  const containerSx = mergeSlotSx(sx, slots?.root?.sx);

  return (
    <Container
      size={['sm', 'sm', 'xl', 'xl', 'xl']}
      overflow="hidden"
      sx={containerSx}
    >
      <Grid
        alignEdges
        spacing={[6, 6, 5]}
        maxWidth={['100vw', '100%']}
        sx={mergeSlotSx(slots?.grid?.sx)}
      >
        <GridItem
          width={[1, 1 / 3]}
          sx={mergeSlotSx(slots?.header?.container?.sx)}
        >
          <Text
            size="xl"
            marginBottom={['1', '2']}
            color={theme.colors.viridian.base}
            textAlign={['center', 'left']}
            sx={mergeSlotSx(slots?.header?.eyebrow?.sx)}
          >
            {eyebrow}
          </Text>

          <Heading
            // @ts-ignore
            size={['32px', '2xl']}
            textAlign={['center', 'left']}
            sx={mergeSlotSx(slots?.header?.title?.sx)}
          >
            {title}
          </Heading>
        </GridItem>

        <GridItem width={[1, 2 / 3]} sx={mergeSlotSx(slots?.content?.sx)}>
          <>
            {isMobile ? (
              <Box
                marginRight="-24px"
                sx={mergeSlotSx(slots?.mobile?.container?.sx)}
              >
                <Box sx={mergeSlotSx(slots?.mobile?.carousel?.sx)}>
                  <Carousel
                    spaceBetween={16}
                    slidesPerView={'auto'}
                    showPagination={cards.length > 1}
                    style={{ paddingRight: '24px' }}
                  >
                    {cards.map((card, index) => (
                      <GridCard
                        key={`grid-card-${index}`}
                        {...card}
                        slots={slots?.card}
                      />
                    ))}
                  </Carousel>
                </Box>
              </Box>
            ) : (
              <Grid spacing={5} sx={mergeSlotSx(slots?.desktop?.grid?.sx)}>
                {cards.map((card, index) => {
                  const firstCardFullWidth = oddNumberOfCards && index === 0;
                  const itemSx = mergeSlotSx(
                    slots?.desktop?.item?.sx,
                    firstCardFullWidth
                      ? slots?.desktop?.itemFullWidth?.sx
                      : undefined,
                  );
                  return (
                    <GridItem
                      key={`grid-card-${index}`}
                      width={[1, firstCardFullWidth ? 1 : 1 / 2]}
                      marginBottom={64}
                      sx={itemSx}
                    >
                      <GridCard
                        {...card}
                        imageStyles={{
                          ...(firstCardFullWidth
                            ? { aspectRatio: 2.5, width: '100%' }
                            : {}),
                        }}
                        slots={slots?.card}
                      />
                    </GridItem>
                  );
                })}
              </Grid>
            )}
          </>
        </GridItem>
      </Grid>
    </Container>
  );
};

// Export ---------

export default GridCardList;

function mergeSlotSx(
  ...sxValues: Array<Record<string, any> | undefined>
): Record<string, any> | undefined {
  const merged = sxValues.reduce((acc, sxValue) => {
    if (!sxValue) {
      return acc;
    }

    return {
      ...acc,
      ...sxValue,
    };
  }, {} as Record<string, any>);

  return Object.keys(merged || {}).length > 0 ? merged : undefined;
}
